"use client";

import { useState, type ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { Section } from "../../primitives";
import DrlpSectionHeader from "../section-header";
import { type DrlpCta } from "../cta";

export interface DrlpComparisonRow {
  /** Mono uppercase row label, e.g. "How it works". */
  feature: string;
  /** Fin (primary) column value. */
  primary: string;
  /** Traditional (secondary) column value. */
  secondary: string;
}

export interface DrlpComparisonTableProps {
  /** Heading for the Fin column / first mobile tab. */
  primaryHeading: string;
  /** Heading for the comparison column / second mobile tab. */
  secondaryHeading: string;
  rows: DrlpComparisonRow[];
  /** Optional eyebrow label above the section heading. Omit to hide. */
  badge?: string;
  /** Optional section heading rendered above the table. Omit to hide. */
  title?: ReactNode;
  /**
   * Optional supporting copy under the title. Pass an array for multiple
   * stacked paragraphs. Only rendered when the header is shown.
   */
  description?: string | string[];
  /** Optional primary (solid) CTA under the header. Omit to hide. */
  primaryCta?: DrlpCta;
  /** Optional secondary (outline) CTA under the header. Omit to hide. */
  secondaryCta?: DrlpCta;
  /** Explicit section id; defaults to a constant when omitted. */
  id?: string;
  /**
   * Tailwind background utility for the accent — the desktop row dot and the
   * mobile active-tab underline. Both sites render under `.brand-i2y4`, so the
   * per-site accent is supplied by the consuming app's DRLP format fn rather
   * than the brand cascade: defaults to Fin orange (`bg-fin`); intercom-com
   * overrides to `bg-helpdesk` (blue).
   */
  markerClassName?: string;
  analyticsId: string;
}

type ColumnKey = "primary" | "secondary";

// Shared per-cell padding so the row-header and value cells line up. Figma:
// mobile 16/24, desktop 18/36 top/bottom.
const CELL_PADDING = "py-4 pr-4 align-top md:pb-9 md:pr-12 md:pt-[18px]";

export default function DrlpComparisonTable({
  primaryHeading,
  secondaryHeading,
  rows,
  badge,
  title,
  description,
  primaryCta,
  secondaryCta,
  id = "comparison-table",
  markerClassName = "bg-fin",
  analyticsId,
}: DrlpComparisonTableProps) {
  const [active, setActive] = useState<ColumnKey>("primary");

  const columns: { key: ColumnKey; heading: string }[] = [
    { key: "primary", heading: primaryHeading },
    { key: "secondary", heading: secondaryHeading },
  ];

  // The header block is optional; show it when there's anything to put in it —
  // any of title, description, or a CTA. Omitting a field from this check would
  // silently drop authored content.
  const hasDescription = Array.isArray(description)
    ? description.length > 0
    : Boolean(description);
  const hasHeader = Boolean(
    badge || title || hasDescription || primaryCta || secondaryCta,
  );

  return (
    <Section
      id={id}
      data-analytics-region="Comparison Table"
      data-analytics-id={analyticsId}
      data-testid="drlp-comparison-table"
      gridCols="10"
      className="md:py-30 py-12"
      dotDecorators
    >
      <div className="col-span-full grid grid-cols-subgrid gap-y-8 md:gap-y-12">
        {hasHeader && (
          <DrlpSectionHeader
            badge={badge}
            title={title}
            description={description}
            primaryCta={primaryCta}
            secondaryCta={secondaryCta}
          />
        )}
        <div className="col-span-full">
          {/* Mobile-only tab control. The table's column-header band isn't shown
            on mobile (no room for both value columns), so these buttons stand in
            visually: they toggle which value column the table shows and slide the
            accent underline. The real <th scope="col"> headers stay in the table
            for screen readers (see thead). On desktop both columns render and
            this is hidden. */}
          <div
            role="group"
            aria-label="Choose a column to compare"
            className="bg-off-white relative flex md:hidden"
          >
            {columns.map((col) => (
              <button
                key={col.key}
                type="button"
                aria-pressed={active === col.key}
                onClick={() => setActive(col.key)}
                className={cn(
                  "text-label-lg flex-1 px-3 py-3 text-center transition-colors",
                  active === col.key ? "text-off-black" : "text-off-black/40",
                )}
              >
                {col.heading}
              </button>
            ))}
            <span
              aria-hidden="true"
              className={cn(
                "absolute bottom-0 left-0 h-0.5 w-1/2 transition-transform duration-200 ease-out",
                markerClassName,
                active === "secondary" && "translate-x-full",
              )}
            />
          </div>

          <table className="w-full table-fixed border-separate border-spacing-0 text-left">
            <caption className="sr-only">
              {primaryHeading} compared with {secondaryHeading}
            </caption>
            <thead>
              {/* On desktop this is the off-white header band. On mobile the
                visible switcher is the tab control above, but the header cells
                stay real `table-cell`s so screen-reader column-header
                associations survive (`display:none`/`sr-only` would blockify the
                th and break `scope="col"`). Their band styling is desktop-only
                and their labels are sr-only on mobile, collapsing the row; the
                inactive column fully collapses to keep the table 2 columns wide. */}
              <tr>
                <th
                  scope="col"
                  className="md:bg-off-white w-1/2 md:w-60 md:py-3"
                >
                  <span className="sr-only">Feature</span>
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      "text-label-lg text-off-black md:bg-off-white font-normal md:py-3",
                      active !== col.key && "hidden md:table-cell",
                    )}
                  >
                    <span className="sr-only md:not-sr-only">
                      {col.heading}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.feature}-${i}`}>
                  <th
                    scope="row"
                    className={cn(
                      "text-label-lg border-clay text-off-black w-1/2 border-t-[0.5px] font-normal md:w-60",
                      CELL_PADDING,
                    )}
                  >
                    {row.feature}
                  </th>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "text-body border-clay border-t-[0.5px]",
                        CELL_PADDING,
                        // Muted second column on desktop; full contrast when it is
                        // the active mobile tab.
                        col.key === "secondary"
                          ? "text-off-black md:text-off-black/80"
                          : "text-off-black",
                        // Hide the inactive value column on mobile. Hiding the same
                        // column index in every row keeps the table aligned.
                        active !== col.key && "hidden md:table-cell",
                      )}
                    >
                      <div className="flex gap-3">
                        {col.key === "primary" && (
                          <span
                            aria-hidden="true"
                            className={cn(
                              "mt-1.5 hidden size-2 shrink-0 md:block",
                              markerClassName,
                            )}
                          />
                        )}
                        <span>
                          {col.key === "primary" ? row.primary : row.secondary}
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}
